<!--
/**
 * APCWO customisation for OrangeHRM — Personal Review module.
 *
 * Licensed under the GNU General Public License v2.0 or later, the same terms
 * as OrangeHRM itself. See the LICENSE file at the project root.
 */
-->

<template>
  <div class="orangehrm-background-container apcwo-pr-root">
    <hr-nav active="reviews" :is-hr="isHr" />
    <oxd-table-filter filter-title="Personal Reviews">
      <oxd-form @submit-valid="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item v-if="isHr">
              <oxd-input-field
                v-model="filters.cycle"
                type="select"
                label="Cycle"
                :options="cycleOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.status"
                type="select"
                label="Status"
                :options="statusOptions"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.nameOrId"
                label="Employee name or ID"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            label="Reset"
            @click="onClickReset"
          />
          <submit-button label="Search" />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="orangehrm-paper-container">
      <table-header :total="total" :loading="isLoading" :selected="0" />
      <div class="orangehrm-container">
        <oxd-card-table
          :headers="headers"
          :items="items?.data"
          :loading="isLoading"
          :selectable="false"
          :clickable="true"
          row-decorator="oxd-table-decorator-card"
          @click="onClickRow"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <review-summary-modal
      v-if="selected"
      :review-id="selected.id"
      :employee-name="selected.employeeName"
      @close="selected = null"
    />
  </div>
</template>

<script>
import {computed, ref} from 'vue';
import {navigate} from '@ohrm/core/util/helper/navigation';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@ohrm/core/util/services/api.service';
import HrNav from '../components/HrNav.vue';
import ReviewSummaryModal from '../components/ReviewSummaryModal.vue';
import {API_BASE, STATUS_LABELS} from '../util/helpers';

export default {
  name: 'ReviewList',
  components: {'hr-nav': HrNav, 'review-summary-modal': ReviewSummaryModal},
  props: {
    isHr: {
      type: Boolean,
      default: false,
    },
    cycleId: {
      type: Number,
      default: null,
    },
  },
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `${API_BASE}/reviews`,
    );
    const filters = ref({
      cycle: props.cycleId
        ? {id: props.cycleId, label: 'Selected cycle'}
        : null,
      status: null,
      nameOrId: '',
    });
    const serialized = computed(() => ({
      cycleId: filters.value.cycle ? filters.value.cycle.id : null,
      status: filters.value.status ? filters.value.status.id : null,
      nameOrId: filters.value.nameOrId,
    }));
    const normalizer = (data) =>
      data.map((item) => ({
        ...item,
        employeeName: item.employee.name,
        cycleName: item.cycle.name,
        statusLabel: STATUS_LABELS[item.status] || item.status,
      }));
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {query: serialized, normalizer});
    return {
      http,
      filters,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      execQuery,
      items: response,
    };
  },
  data() {
    return {
      selected: null,
      cycleOptions: [],
      statusOptions: Object.keys(STATUS_LABELS).map((id) => ({
        id,
        label: STATUS_LABELS[id],
      })),
      headers: [
        {
          name: 'employeeName',
          title: 'Employee',
          style: {'flex-basis': '24%'},
        },
        {
          name: 'cycleName',
          title: 'Cycle',
          style: {'flex-basis': '24%'},
        },
        {name: 'team', title: 'Team', style: {'flex-basis': '16%'}},
        {
          name: 'statusLabel',
          title: 'Status',
          style: {'flex-basis': '12%'},
        },
        {
          name: 'submittedAt',
          title: 'Submitted',
          style: {'flex-basis': '16%'},
        },
        {
          name: 'actions',
          title: 'Actions',
          style: {'flex-basis': '8%'},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionsCell,
        },
      ],
    };
  },
  created() {
    if (!this.isHr) return;
    new APIService(window.appGlobal.baseUrl, `${API_BASE}/cycles`)
      .getAll({limit: 100})
      .then((response) => {
        this.cycleOptions = response.data.data.map((c) => ({
          id: c.id,
          label: c.name,
        }));
        if (this.filters.cycle) {
          const match = this.cycleOptions.find(
            (o) => o.id === this.filters.cycle.id,
          );
          if (match) this.filters.cycle = match;
        }
      });
  },
  methods: {
    async filterItems() {
      this.currentPage = 1;
      await this.execQuery();
    },
    async onClickReset() {
      this.filters.cycle = null;
      this.filters.status = null;
      this.filters.nameOrId = '';
      await this.filterItems();
    },
    actionsCell(...[, , , row]) {
      const cellConfig = {};
      if (row.permissions && row.permissions.editHr) {
        cellConfig.edit = {
          onClick: this.onClickAddInfo,
          props: {name: 'pencil-fill', title: 'Add Info'},
        };
      }
      return {props: {header: {cellConfig}}};
    },
    onClickAddInfo($event) {
      const row = $event.id ? $event : $event.item;
      navigate(
        '/personalReview/viewPersonalReview/{id}',
        {id: row.id},
        {tab: 'hr'},
      );
    },
    onClickRow(item) {
      const row = item.id ? item : item.item;
      if (row && row.id) this.selected = row;
    },
  },
};
</script>
